import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useProducts } from '../../context/ProductContext';
import { getCategoryTabs } from '../../utils/productUtils';
import Spinner from '../../components/common/Spinner';

const COLORS = {
  header: '#2C3640',
  background: '#F4F3F8',
  primary: '#4A6741',
  active: '#E85830',
};

const ProductsPage = () => {
  const navigate = useNavigate();
  const { products, loading } = useProducts();
  const [activeCategory, setActiveCategory] = useState('전체');
  const tabRef = useRef(null);

  // 기본 분류 + 직접 입력해 등록한 분류까지 탭으로 표시
  const categories = ['전체', ...getCategoryTabs(products)];

  const filtered = activeCategory === '전체'
    ? products
    : products.filter(p => p.category1 === activeCategory || p.category === activeCategory);

  return (
    <Container>
      <TabRow ref={tabRef}>
        {categories.map(cat => (
          <TabBtn
            key={cat}
            $active={activeCategory === cat}
            onClick={() => setActiveCategory(cat)}
          >
            {cat}
          </TabBtn>
        ))}
      </TabRow>

      <ListCount>{filtered.length}개 상품</ListCount>

      <ProductList>
        {filtered.map(product => (
          <ProductCard key={product.id} $soldOut={product.soldOut} onClick={() => navigate(`/products/${product.id}`)}>
            {product.image ? (
              <ImgWrap>
                <ProductImg src={product.image} alt={product.name} onLoad={e => e.target.style.opacity = 1} />
                {product.soldOut && <SoldOutBadge>품절</SoldOutBadge>}
              </ImgWrap>
            ) : (
              <ThumbWrap>
                {product.name.slice(0, 2)}
              </ThumbWrap>
            )}
            <ProductInfo>
              <SubCategory>{product.category2 || product.category1 || product.category || ''}</SubCategory>
              <ProductName>{product.name}</ProductName>
              {product.summary && <ProductSummary>{product.summary}</ProductSummary>}
            </ProductInfo>
          </ProductCard>
        ))}
      </ProductList>

      {loading && <Spinner variant="dots" size="md" text="상품 불러오는 중..." fullPage />}
      {!loading && filtered.length === 0 && <Empty>등록된 상품이 없습니다</Empty>}
    </Container>
  );
};

export default ProductsPage;

const Container = styled.div`
  min-height: 100vh;
  background: ${COLORS.background};
  padding-bottom: 100px;
`;

const TabRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { display: none; }
`;

const TabBtn = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: ${p => p.$active ? '600' : '400'};
  white-space: nowrap;
  flex-shrink: 0;
  background: ${p => p.$active ? COLORS.primary : '#fff'};
  color: ${p => p.$active ? '#fff' : '#666'};
  border: 1px solid ${p => p.$active ? COLORS.primary : '#E0E0E0'};
  transition: all 0.15s;
`;

const ListCount = styled.p`
  font-size: 12px;
  color: #8C9AA4;
  padding: 0 20px 8px;
`;

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px;
`;

const ProductCard = styled.div`
  display: flex;
  gap: 14px;
  padding: 12px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  cursor: pointer;
  align-items: center;
  transition: all 0.15s;
  opacity: ${p => p.$soldOut ? 0.5 : 1};
  &:active { transform: scale(0.98); }
`;

const ImgWrap = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
  background: #ECEEE9;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%);
    animation: shimmer 1.5s infinite;
  }
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const ProductImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: relative;
  z-index: 1;
  background: #F5F5F0;
  opacity: 0;
  transition: opacity 0.4s ease;
`;

const ThumbWrap = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 10px;
  background: #EDF2EB;
  color: ${COLORS.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
`;

const ProductInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SubCategory = styled.p`
  font-size: 11px;
  color: ${COLORS.primary};
  font-weight: 500;
  margin-bottom: 4px;
`;

const ProductName = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #1E2A32;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

const ProductSummary = styled.p`
  font-size: 12px;
  color: #8C9AA4;
  margin-top: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SoldOutBadge = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 2px;
  border-radius: 10px;
`;

const Empty = styled.p`
  text-align: center;
  padding: 48px;
  color: #8C9AA4;
  font-size: 14px;
`;
